import {Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {Location} from '@angular/common';
import {Subject} from 'rxjs';
import {fuseAnimations} from '@fuse/animations';
import {ActivatedRoute} from '@angular/router';
import {UtilMessage} from '../../../util/util-message';
import {FormBuilder, FormGroup} from '@angular/forms';
import {EventModel} from '../../model/event.model';
import {FileInput, FileInputComponent, FileValidator} from 'ngx-material-file-input';
import {EventService} from '../event.service';
import {MatEndDate, MatStartDate} from "@angular/material/datepicker";
import * as moment from 'moment';
import {Moment} from 'moment';
import {ContactModel} from "../../model/contact.model";
import {ContactDialog} from "../../announcement/contact/contact-dialog.component";
import {User} from "../../model/user.model";
import {MatDialog} from "@angular/material/dialog";
import {ContactService} from "../../announcement/contact/contact-dialog.service";
import {State} from "../../model/state.model";
import {UserService} from "../../user/user.service";
import {AddressModel} from "../../model/address.model";
import {ZipCodeService} from "../../../util/zip-code.service";
import {FileModel, MimeHelper} from "../../model/file.model";

@Component({
    selector: 'edit-event',
    templateUrl: './event-save.component.html',
    styleUrls: ['./event-save.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class EventSaveComponent implements OnInit, OnDestroy, ContactService {

    event: EventModel;
    isUpdate = true;
    isClearImage = false;
    saveForm: FormGroup;
    imageFileInput: FileInput;
    image: string;
    imageContentType: string;
    nomeArquivo: string;
    private _unsubscribeAll: Subject<any>;
    private maxSize = 2;
    states: Array<State>;
    contacts: Array<ContactModel> = [];
    users: Array<User> = [];

    @ViewChild(MatStartDate) dateBegin: MatStartDate<Moment>;
    @ViewChild(MatEndDate) dateEnd: MatEndDate<Moment>;

    constructor(private _location: Location,
                private _route: ActivatedRoute,
                private _message: UtilMessage,
                private dialog: MatDialog,
                private _formBuilder: FormBuilder,
                private _userService: UserService,
                private _zipCodeService: ZipCodeService,
                private _eventService: EventService) {
        this.event = new EventModel();
        this.event.address = new AddressModel();
        this._unsubscribeAll = new Subject();
        this._userService.findAll().toPromise().then(value => this.users = value.body);
        this.states = State.getStates();
    }

    ngOnInit(): void {
        this._route.params.subscribe(params => {
            const id = params['id'];
            this.isUpdate = id !== 'new';
            this.loadEvent(id);
        });
        this.saveForm = this.createForm();
    }

    onKeyUp(zipCode): void {
        if (zipCode !== null && zipCode.target.value.length === 9) {
            this._zipCodeService.get(this.event.address.zipCode).toPromise().then(response => {
                this.event.address.street = response.logradouro;
                this.event.address.district = response.bairro;
                this.event.address.city = response.localidade;
                this.event.address.state = response.uf;
            });
        }
    }

    private createForm(): FormGroup {
        return this._formBuilder.group({
            code: [''],
            title: [''],
            contacts: [''],
            user: [''],
            district: [''],
            zipCode: [''],
            street: [''],
            number: [''],
            state: [''],
            city: [''],
            complement: [''],
            dateBegin: [{ value: ''}],
            dateEnd: [{ value: ''}],
            nomeArquivo: [''],
            imageContentType: [null],
            image: ['', [FileValidator.maxContentSize(this.maxSize * 1048576)]],
            description: [''],
        });
    }

    private loadEvent(id): void {
        if (this.isUpdate) {
            this._eventService.find(id).toPromise().then(result => {
                this.event = result.body;
                this.dateBegin.value = this.event.dateBegin;
                this.dateEnd.value = this.event.dateEnd;
                this.contacts = this.event.contacts;
                this.image = this.event.image;
                this.imageContentType = this.event.contentType;
                this.nomeArquivo = this.event.nomeArquivo;
                if(this.event.address == null) this.event.address = new AddressModel();
                this.saveForm.patchValue(this.event);
            });
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    save(event: EventModel): void {
        event.image = this.image;
        event.dateBegin = this.dateBegin.value;
        event.dateEnd = this.dateEnd.value;
        event.nomeArquivo = this.nomeArquivo;
        event.contentType = this.imageContentType;
        this._eventService.create(event).toPromise()
            .then((value) => {
                this._message.success('Registro salvo com sucesso!');
                this.close();
            }).catch((error) => this._message.error(error));
    }

    update(event: EventModel): void {
        event.image = this.image;
        event.nomeArquivo = this.nomeArquivo;
        event.contentType = this.imageContentType;
        event.dateBegin = this.dateBegin.value;
        event.dateEnd = this.dateEnd.value;
        this._eventService.update(event).toPromise()
            .then(() => {
                this._message.success('Registro atualizado com sucesso!');
                this.close();
            }).catch((error) => this._message.error(error));
    }

    close(): void {
        this._location.back();
    }

    uploadDocument(value): void {
        const file = value.target.files[0];
        const ext = FileModel.getFileExtension(file.name);
        const m = MimeHelper.findByExtension(ext);

        const reader = new FileReader();
        reader.readAsBinaryString(file);
        reader.onload = () => {
            this.imageContentType = m.documentoContentType;
            this.nomeArquivo = file.name;
            this.image = btoa(reader.result.toString());
            this.saveForm.patchValue({
                'nomeArquivo': file.name,
                'image': this.image,
                'imageContentType': m.documentoContentType
            });
        };
    }

    clearFile() {
        this.image = null;
        this.isClearImage = true;
        this.nomeArquivo = null;
        this.imageContentType = null;
        this.saveForm.patchValue({
            'nomeArquivo': null,
            'image': null,
            'imageContentType': null
        });
    }

    compareID(one: any, two: any): boolean {
        return one && two ? one.id === two.id : one === two;
    }

    addContact(contact: ContactModel) {
        this.contacts.push(contact);
    }

    openContact(): void {
        const dialogRef = this.dialog.open(ContactDialog, {
            width: '30%',
            disableClose: true
        });
        dialogRef.componentInstance.contactService = this;
    }

    getImageUrl(id: number): string {
        return `https://chibarro-grei-prod-public-assets.s3.amazonaws.com/events/${id}.png?v=${new Date().getTime()}`;  // Adiciona um timestamp
    }
}
